import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Award, Plus, History, Trophy, Star, Sparkles, CheckCircle } from "lucide-react";
import { API } from "../App";
import ExerciseIntro from "../components/ExerciseIntro";
import { getExerciseInfo } from "../lib/exerciseData";
import { useI18n } from "../lib/i18n";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

const REWARD_IDS = ["reward1", "reward2", "reward3", "reward4", "reward5", "reward6", "reward7", "reward8"];

const SelfReinforcementPage = ({ user, logout }) => {
  const { t } = useI18n();
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const EXERCISE_INFO = getExerciseInfo(t);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    accomplishment: "",
    difficulty_level: "moyen",
    emotions_felt: "",
    reward_chosen: "",
    custom_reward: "",
    reflection: "",
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/self-reinforcement`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.accomplishment) {
      toast.error(t("reinforcement.describeAccomplishment"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/self-reinforcement`, {
        accomplishment: formData.accomplishment,
        difficulty_level: formData.difficulty_level,
        emotions_felt: formData.emotions_felt,
        reward_chosen: formData.custom_reward || formData.reward_chosen,
        reflection: formData.reflection,
      }, { withCredentials: true });
      toast.success(t("reinforcement.saved") || "Saved!");
      if (user?.role === "patient") promptShare(res.data, "self_reinforcement");
      setFormData({ accomplishment: "", difficulty_level: "moyen", emotions_felt: "", reward_chosen: "", custom_reward: "", reflection: "" });
      fetchEntries();
    } catch (e) {
      toast.error(t("common.error") || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const difficultyOptions = [
    { value: "facile", label: t("reinforcement.easy"), color: "bg-green-100 text-green-700 border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-800" },
    { value: "moyen", label: t("reinforcement.medium"), color: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800" },
    { value: "difficile", label: t("reinforcement.hard"), color: "bg-red-100 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-800" },
  ];

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8" data-testid="self-reinforcement-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("reinforcement.title")}</h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("reinforcement.subtitle")}</p>
        </div>

        <ExerciseIntro exerciseInfo={EXERCISE_INFO.self_reinforcement} />

        <div className="grid lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Plus className="w-5 h-5" />
                {t("reinforcement.newAccomplishment")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("reinforcement.whatAccomplished")}</label>
                <Textarea
                  placeholder={t("reinforcement.accomplishmentPlaceholder")}
                  value={formData.accomplishment}
                  onChange={(e) => setFormData({ ...formData, accomplishment: e.target.value })}
                  rows={3}
                  data-testid="accomplishment-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("reinforcement.difficulty")}</label>
                <div className="flex gap-2">
                  {difficultyOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setFormData({ ...formData, difficulty_level: opt.value })}
                      className={`px-4 py-2 rounded-full text-sm border transition-all ${
                        formData.difficulty_level === opt.value ? opt.color : "bg-background border-border text-muted-foreground hover:bg-muted"
                      }`}
                      data-testid={`difficulty-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("reinforcement.emotionsFelt")}</label>
                <Input
                  placeholder={t("reinforcement.emotionsPlaceholder")}
                  value={formData.emotions_felt}
                  onChange={(e) => setFormData({ ...formData, emotions_felt: e.target.value })}
                  data-testid="emotions-felt-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("reinforcement.chooseReward")}</label>
                <div className="flex flex-wrap gap-2">
                  {REWARD_IDS.map((id) => { const reward = t(`reinforcement.${id}`); return (
                    <button
                      key={reward}
                      onClick={() => setFormData({ ...formData, reward_chosen: reward, custom_reward: "" })}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                        formData.reward_chosen === reward && !formData.custom_reward
                          ? "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800"
                          : "bg-background border-border hover:bg-muted"
                      }`}
                    >
                      {reward}
                    </button>
                  ); })}
                </div>
                <Input
                  placeholder={t("reinforcement.customReward")}
                  value={formData.custom_reward}
                  onChange={(e) => setFormData({ ...formData, custom_reward: e.target.value })}
                  data-testid="custom-reward-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("exercise.reflection")}</label>
                <Textarea
                  placeholder={t("reinforcement.reflectionPlaceholder")}
                  value={formData.reflection}
                  onChange={(e) => setFormData({ ...formData, reflection: e.target.value })}
                  rows={2}
                  data-testid="reflection-input"
                />
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90"
                data-testid="submit-reinforcement-btn"
              >
                {submitting ? t("exercise.saving") : t("reinforcement.celebrate")}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                {t("reinforcement.myAccomplishments")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse h-20 bg-muted rounded-lg" />
                  ))}
                </div>
              ) : entries.length > 0 ? (
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                  {entries.map((entry) => (
                    <div key={entry.entry_id} className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 dark:bg-purple-950 dark:text-purple-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Star className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{entry.accomplishment}</p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              entry.difficulty_level === "facile" ? "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300" :
                              entry.difficulty_level === "difficile" ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" :
                              "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                            }`}>
                              {entry.difficulty_level}
                            </span>
                            {entry.reward_chosen && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300">
                                {entry.reward_chosen}
                              </span>
                            )}
                          </div>
                          {entry.reflection && (
                            <p className="text-xs text-muted-foreground mt-1 italic">"{entry.reflection}"</p>
                          )}
                          <p className="text-xs text-muted-foreground text-sm sm:text-base mt-1">
                            {new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(entry.created_at))}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t("reinforcement.noAccomplishments")}</p>
                  <p className="text-sm text-muted-foreground text-sm sm:text-base mt-1">{t("reinforcement.startCelebrating")}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default SelfReinforcementPage;
